import React, { useEffect, useState } from 'react';
import { Filter, SlidersHorizontal } from 'lucide-react';
import { ProductCard } from '../components/ProductCard';
import { SEO } from '../components/SEO';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Gallery = () => {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'rings'); // По умолчанию rings
  const [sortBy, setSortBy] = useState('in_stock');
  const [loading, setLoading] = useState(true);
  const [gallerySettings, setGallerySettings] = useState(null);
  const [pageContent, setPageContent] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  
  // Адаптивное количество продуктов на странице
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  const lang = i18n.language;
  
  // Определяем количество продуктов в зависимости от размера экрана
  useEffect(() => {
    const updateItemsPerPage = () => {
      if (window.innerWidth < 768) {
        // Мобильные устройства: 5 продуктов
        setItemsPerPage(5);
      } else {
        // Компьютеры: 10 продуктов
        setItemsPerPage(10);
      }
    };
    
    updateItemsPerPage();
    window.addEventListener('resize', updateItemsPerPage);
    
    return () => window.removeEventListener('resize', updateItemsPerPage);
  }, []);
  
  // Сброс страницы при смене категории
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory, sortBy]);

  // Скролл к товарам при смене страницы
  useEffect(() => {
    if (currentPage > 1 || currentPage === 1) {
      const productsGrid = document.getElementById('products-grid');
      if (productsGrid) {
        productsGrid.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, [currentPage]);

  useEffect(() => {
    // Загружаем категории и настройки параллельно
    Promise.all([
      fetchCategories(),
      fetchGallerySettings(),
      fetchPageContent()
    ]);
  }, []);

  useEffect(() => {
    // Загружаем продукты только при изменении фильтров
    const abortController = new AbortController();
    
    const loadProducts = async () => {
      setLoading(true);
      setProducts([]); // ОЧИЩАЕМ продукты перед загрузкой
      
      try {
        let url = `${API}/products`;
        const params = new URLSearchParams();
        
        params.append('category', selectedCategory);
        
        if (sortBy && sortBy !== 'custom_all') {
          params.append('sort_by', sortBy);
        }
        
        if (params.toString()) {
          url += `?${params.toString()}`;
        }
        
        const response = await axios.get(url, {
          signal: abortController.signal // Отмена запроса при размонтировании
        });
        
        // Фильтруем на клиенте для дополнительной защиты
        const filteredProducts = response.data.filter(p => p.category === selectedCategory);
        
        if (!abortController.signal.aborted) {
          setProducts(filteredProducts);
        }
      } catch (error) {
        if (error.name !== 'CanceledError') {
          console.error('Error fetching products:', error);
          setProducts([]);
        }
      } finally {
        if (!abortController.signal.aborted) {
          setLoading(false);
        }
      }
    };
    
    loadProducts();
    
    // Отменяем запрос при смене категории/сортировки
    return () => {
      abortController.abort();
    };
  }, [selectedCategory, sortBy]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      // Фильтруем - оставляем только кольца и серьги
      const filtered = response.data.filter(c => c.id === 'rings' || c.id === 'earrings');
      setCategories(filtered);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchGallerySettings = async () => {
    try {
      const response = await axios.get(`${API}/gallery-settings`);
      setGallerySettings(response.data);
    } catch (error) {
      console.error('Error fetching gallery settings:', error);
    }
  };

  const fetchPageContent = async () => {
    try {
      const response = await axios.get(`${API}/pages-content`);
      if (response.data && response.data.pages && response.data.pages.gallery) {
        setPageContent(response.data.pages.gallery);
      }
    } catch (error) {
      console.error('Error fetching page content:', error);
    }
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    if (category === 'all') {
      searchParams.delete('category');
    } else {
      searchParams.set('category', category);
    }
    setSearchParams(searchParams);
  };

  // Хелпер для получения текста с фоллбэком
  const getText = (field, fallbackRu, fallbackLv) => {
    if (pageContent && pageContent[field]) {
      if (typeof pageContent[field] === 'object') {
        return pageContent[field][lang] || pageContent[field].ru || (lang === 'lv' ? fallbackLv : fallbackRu);
      }
      return pageContent[field];
    }
    return lang === 'lv' ? fallbackLv : fallbackRu;
  };

  return (
    <div className="min-h-screen pt-32 pb-24 px-6 md:px-12" data-testid="gallery-page">
      <SEO 
        title={getText('page_title', 'Коллекция', 'Kolekcija')}
        description={getText('page_description', 'Откройте для себя изысканные украшения ручной работы', 'Atklājiet izsmalcinātus rokdarbu rotaslietas')}
      />
      <div className="max-w-7xl mx-auto">
        <div className="mb-16">
          <h1 className="font-heading text-5xl md:text-6xl mb-4 tracking-tight" data-testid="gallery-title">
            {getText('page_title', 'Коллекция', 'Kolekcija')}
          </h1>
          <p className="font-body text-lg text-muted-foreground tracking-wide">
            {getText('page_description', 'Откройте для себя изысканные украшения ручной работы', 'Atklājiet izsmalcinātus rokdarbu rotaslietas')}
          </p>
        </div>

        <div className="flex flex-col md:flex-row gap-6 mb-12 items-start md:items-center justify-between" data-testid="filter-bar">
          <div className="flex flex-wrap gap-3">
            {categories.map((category) => (
              <Button
                key={category.id}
                variant={selectedCategory === category.id ? 'default' : 'outline'}
                onClick={() => handleCategoryChange(category.id)}
                className="uppercase tracking-widest transition-colors duration-500"
                data-testid={`filter-${category.id}`}
              >
                {category.name[lang] || category.name.ru} ({category.count})
              </Button>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <SlidersHorizontal className="w-4 h-4 text-muted-foreground" />
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[220px]" data-testid="sort-select">
                <SelectValue placeholder={t('gallery.sort')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="in_stock" data-testid="sort-in-stock">
                  {lang === 'lv' ? 'Vispirms noliktavā' : 'Сначала в наличии'}
                </SelectItem>
                <SelectItem value="popularity" data-testid="sort-popularity">{t('gallery.popularity')}</SelectItem>
                <SelectItem value="price_asc" data-testid="sort-price-asc">{t('gallery.priceAsc')}</SelectItem>
                <SelectItem value="price_desc" data-testid="sort-price-desc">{t('gallery.priceDesc')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-24" data-testid="loading-indicator">
            <p className="font-body text-muted-foreground">{t('gallery.loading')}</p>
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-24" data-testid="no-products">
            <p className="font-body text-muted-foreground">{t('gallery.noProducts')}</p>
          </div>
        ) : (
          <>
            <div 
              id="products-grid"
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto mb-12"
              data-testid="products-grid"
            >
              {products.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((product, index) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  index={index}
                  featured={false}
                  cardSize={gallerySettings?.card_size || 'small'}
                  showPrice={gallerySettings?.show_price ?? true}
                  showCategory={gallerySettings?.show_category ?? true}
                />
              ))}
            </div>
            
            {/* Пагинация */}
            {products.length > itemsPerPage && (
              <div className="flex justify-center items-center gap-4 mt-12">
                <Button
                  variant="outline"
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="uppercase tracking-widest"
                >
                  {lang === 'lv' ? 'Iepriekšējā' : 'Предыдущая'}
                </Button>
                
                <div className="flex gap-2">
                  {Array.from({ length: Math.ceil(products.length / itemsPerPage) }, (_, i) => i + 1).map(page => (
                    <Button
                      key={page}
                      variant={currentPage === page ? 'default' : 'outline'}
                      onClick={() => setCurrentPage(page)}
                      className="w-10 h-10 p-0"
                    >
                      {page}
                    </Button>
                  ))}
                </div>
                
                <Button
                  variant="outline"
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(products.length / itemsPerPage)))}
                  disabled={currentPage === Math.ceil(products.length / itemsPerPage)}
                  className="uppercase tracking-widest"
                >
                  {lang === 'lv' ? 'Nākamā' : 'Следующая'}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
